import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@referee/analytics';
import { useTestData } from './TestDataContext';
import { calculateScore } from '@referee/scoring';
import type {
  TestModeConfig,
  TestQuestionResult,
  ScoringMethod,
} from '../model/test-mode-types';

type TestStatus = 'setup' | 'active' | 'finished';

export interface TestModeContextValue {
  config: TestModeConfig;
  status: TestStatus;
  questionIds: string[];
  currentIndex: number;
  answers: Record<string, string[]>;
  results: TestQuestionResult[];
  startTime: string | null;
  totalScore: number;
  maxScore: number;
  passed: boolean;
  startTest: (config: TestModeConfig) => void;
  setAnswer: (questionId: string, answers: string[]) => void;
  goToQuestion: (index: number) => void;
  finishTest: (timedOut?: boolean) => Promise<void>;
  resetTest: () => void;
}

const defaultConfig: TestModeConfig = {
  questionCount: 10,
  timeLimitMinutes: null,
  passPercentage: 70,
  scoringMethod: 'BINARY',
  ruleFilter: [],
};

const TestModeCtx = createContext<TestModeContextValue | null>(null);

export function useTestMode(): TestModeContextValue {
  const ctx = useContext(TestModeCtx);
  if (!ctx) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return ctx;
}

function selectRandomQuestions(
  data: Record<string, { id: string; rule: string }>,
  count: number,
  ruleFilter: string[],
): string[] {
  let ids = Object.keys(data);
  if (ruleFilter.length > 0) {
    ids = ids.filter((id) => ruleFilter.includes(data[id].rule));
  }
  const shuffled = ids.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

function computeResults(
  questionIds: string[],
  answersMap: Record<string, string[]>,
  data: Record<string, { correct: string[]; getAnswers: (lang: string) => Record<string, string> }>,
  scoringMethod: ScoringMethod,
  language: string,
): TestQuestionResult[] {
  return questionIds.map((qId) => {
    const question = data[qId];
    const selected = answersMap[qId] ?? [];
    const correct = question.correct;
    const options = Object.keys(question.getAnswers(language));

    const { score, maxScore } = calculateScore(options, correct, selected, scoringMethod);

    const sortedSelected = [...selected].sort();
    const sortedCorrect = [...correct].sort();
    const isCorrect =
      sortedSelected.length === sortedCorrect.length &&
      sortedSelected.every((v, i) => v === sortedCorrect[i]);

    return { questionId: qId, selectedAnswers: selected, correctAnswers: correct, isCorrect, score, maxScore };
  });
}

export function TestModeProvider({ children }: { children: ReactNode }) {
  const { data, recordAnswer } = useTestData();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [config, setConfig] = useState<TestModeConfig>(defaultConfig);
  const [status, setStatus] = useState<TestStatus>('setup');
  const [questionIds, setQuestionIds] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [results, setResults] = useState<TestQuestionResult[]>([]);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [passed, setPassed] = useState(false);

  const startTest = useCallback((newConfig: TestModeConfig) => {
    const selected = selectRandomQuestions(data, newConfig.questionCount, newConfig.ruleFilter);

    let availableQuestions = Object.keys(data).length;
    if (newConfig.ruleFilter.length > 0) {
      availableQuestions = Object.keys(data).filter((id) =>
        newConfig.ruleFilter.includes(data[id].rule),
      ).length;
    }

    trackEvent('test_mode_started', {
      question_count: selected.length,
      time_limit_minutes: newConfig.timeLimitMinutes,
      pass_percentage: newConfig.passPercentage,
      scoring_method: newConfig.scoringMethod,
      rule_filter_count: newConfig.ruleFilter.length,
      available_questions: availableQuestions,
    });

    setConfig(newConfig);
    setQuestionIds(selected);
    setCurrentIndex(0);
    setAnswers({});
    setResults([]);
    setStartTime(new Date().toISOString());
    setTotalScore(0);
    setMaxScore(0);
    setPassed(false);
    setStatus('active');
    navigate('/test/active');
  }, [data, navigate]);

  const setAnswer = useCallback((questionId: string, selectedAnswers: string[]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedAnswers }));
  }, []);

  const goToQuestion = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const finishTest = useCallback(async (timedOut = false) => {
    for (const qId of questionIds) {
      const selected = answers[qId] ?? [];
      await recordAnswer(qId, selected);
    }

    const language = i18n.language;
    const computed = computeResults(questionIds, answers, data, config.scoringMethod, language);
    const total = computed.reduce((sum, r) => sum + r.score, 0);
    const max = computed.reduce((sum, r) => sum + r.maxScore, 0);
    const passThreshold = max * (config.passPercentage / 100);
    const didPass = total >= passThreshold;

    const questionsAnswered = questionIds.filter((qId) => (answers[qId]?.length ?? 0) > 0).length;
    const durationSeconds = startTime
      ? Math.round((Date.now() - new Date(startTime).getTime()) / 1000)
      : 0;

    trackEvent('test_mode_finished', {
      total_score: total,
      max_score: max,
      pass_percentage: config.passPercentage,
      passed: didPass,
      scoring_method: config.scoringMethod,
      question_count: questionIds.length,
      questions_answered: questionsAnswered,
      duration_seconds: durationSeconds,
      timed_out: timedOut,
    });

    setResults(computed);
    setTotalScore(total);
    setMaxScore(max);
    setPassed(didPass);
    setStatus('finished');
    navigate('/test/results');
  }, [questionIds, answers, recordAnswer, i18n.language, data, config, startTime, navigate]);

  const resetTest = useCallback(() => {
    trackEvent('test_mode_reset', { previous_passed: passed });

    setStatus('setup');
    setQuestionIds([]);
    setCurrentIndex(0);
    setAnswers({});
    setResults([]);
    setStartTime(null);
    setTotalScore(0);
    setMaxScore(0);
    setPassed(false);
    navigate('/test');
  }, [passed, navigate]);

  const value = useMemo<TestModeContextValue>(
    () => ({
      config,
      status,
      questionIds,
      currentIndex,
      answers,
      results,
      startTime,
      totalScore,
      maxScore,
      passed,
      startTest,
      setAnswer,
      goToQuestion,
      finishTest,
      resetTest,
    }),
    [config, status, questionIds, currentIndex, answers, results, startTime, totalScore, maxScore, passed, startTest, setAnswer, goToQuestion, finishTest, resetTest],
  );

  return <TestModeCtx value={value}>{children}</TestModeCtx>;
}
